// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DemandScheduleRow } from '@/project/utilities/build-demand-schedule';
import { AxisScale } from '@visx/axis';

type DateScale = AxisScale<number>;
type LinearScale = AxisScale<number>;

interface StepPaths {
  area: string;
  line: string;
}

/**
 * Builds the SVG path strings for a single SKU's step-after demand curve.
 * Steps can land outside the axis domain. Steps before the visible range are
 * clamped to the start so the range begins at the active value, while steps
 * after the visible range are ignored so the curve holds through the inclusive
 * range end without bleeding past the chart edge.
 */
export function buildStepPaths(
  row: DemandScheduleRow,
  dateScale: DateScale,
  yScale: LinearScale,
  domainStartX: number,
  domainEndX: number,
  rowHeight: number
): StepPaths | null {
  if (!row.hasDatedDemand) return null;

  const baseY = yScale(row.baseline) ?? rowHeight;
  const points: [number, number][] = [[domainStartX, baseY]];
  let currentY = baseY;
  for (const step of row.steps) {
    const rawX = dateScale(new Date(step.timeMs)) ?? domainStartX;
    if (rawX > domainEndX) break;
    const x = Math.max(rawX, domainStartX);
    const newY = yScale(row.baseline + step.datedQuantity) ?? baseY;
    points.push([x, currentY]);
    points.push([x, newY]);
    currentY = newY;
  }
  points.push([domainEndX, currentY]);

  const linePath = points
    .map(
      ([x, y], index) => `${index === 0 ? 'M' : 'L'} ${String(x)} ${String(y)}`
    )
    .join(' ');

  const areaPath = `${linePath} L ${String(domainEndX)} ${String(baseY)} L ${String(domainStartX)} ${String(baseY)} Z`;

  return { area: areaPath, line: linePath };
}
